/**
 * Município estimado pela localização (para focos do GOES-19 de 10 min, que
 * não trazem município). Ponto-no-polígono sobre a camada `municipios` do
 * módulo de vetores — reaproveita o carregador (`getLoadedSource` /
 * `ensureDataSource`), só importando. Se a camada estiver desligada, os dados
 * são baixados no primeiro clique (e ficam em cache). Calculado só no clique (popup).
 */
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type VectorSource from 'ol/source/Vector';
import { ensureDataSource, getLoadedSource } from '../vectors/loader';

export interface EstimatedMunicipio {
  nome: string;
  uf: string | null;
  cdMun: string | null;
}

/** Município (feature) que contém a coordenada do mapa, ou null. */
export function municipioAt(source: VectorSource<Feature<Geometry>>, x: number, y: number): EstimatedMunicipio | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  for (const f of source.getFeaturesAtCoordinate([x, y])) {
    const nome = f.get('nome');
    if (typeof nome !== 'string' || !nome) continue;
    const uf = f.get('uf');
    const cd = f.get('cd_mun');
    return { nome, uf: typeof uf === 'string' ? uf : null, cdMun: cd != null ? String(cd) : null };
  }
  return null;
}

/** Com os municípios já em memória: resultado imediato; `undefined` se ainda não carregados. */
export function estimateMunicipioNow(x: number, y: number): EstimatedMunicipio | null | undefined {
  const source = getLoadedSource('municipios') as VectorSource<Feature<Geometry>> | undefined;
  return source ? municipioAt(source, x, y) : undefined;
}

/** Carrega os municípios (se preciso) e estima. */
export async function estimateMunicipio(x: number, y: number): Promise<EstimatedMunicipio | null> {
  const source = (await ensureDataSource('municipios')) as VectorSource<Feature<Geometry>> | null;
  return source ? municipioAt(source, x, y) : null;
}
